import type { CatalogueEntry, Settings } from "./api";

/**
 * Searching the catalogue in the browser, on every keystroke (M1.13 §2).
 *
 * Pure, and its own module, so the rules are pinned in `test/search.test.ts` rather than living
 * inside a component. They are the rules Shopify's own search had, which the panel used until
 * M1.13 and hosts had learned — minus the round trip, which is the whole point:
 *
 * - **Every word must match**, and a word matches by **prefix**: `choc bar` finds *Chocolate Bar*,
 *   and so does `ch b`. Case and accents are folded.
 * - **Title, variant name and SKU** are all searched, as one set of words.
 * - **An exact SKU leads**, because a host reading a SKU off a box wants that item and nothing
 *   near it. Then titles that *start* with the first word, then everything else alphabetically.
 *
 * At about eleven hundred products this is a linear scan of a few thousand prepared rows per
 * keystroke — well under a millisecond — so there is no index to keep and nothing to go stale.
 */

/** Lower case, accents off, and anything that is not a letter or a digit is a space. */
export function fold(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function words(text: string): string[] {
  const folded = fold(text);
  return folded ? folded.split(" ") : [];
}

/** An entry with its words worked out once, rather than on every keystroke. */
export interface Prepared {
  entry: CatalogueEntry;
  words: string[];
  title: string;
  sku: string;
}

export function prepare(entry: CatalogueEntry): Prepared {
  const title = fold(
    entry.variantTitle
      ? `${entry.productTitle} ${entry.variantTitle}`
      : entry.productTitle,
  );
  const sku = entry.sku ? fold(entry.sku) : "";
  return {
    entry,
    words: [...new Set([...title.split(" "), ...sku.split(" ")].filter(Boolean))],
    title,
    sku,
  };
}

/** Every query word is the start of some word of the entry. */
function matches(prepared: Prepared, query: string[]): boolean {
  return query.every((word) =>
    prepared.words.some((candidate) => candidate.startsWith(word)),
  );
}

/**
 * Whether a variant can be featured, as far as the panel can tell from the copy, and if not why.
 *
 * - **`disabled`** — no SKU, or a blank one. Never sold from a show (`validSku` on the server), and
 *   the one reason that is not about stock, so it is named apart.
 * - **`untracked`**, **`no-location`** — Shopify cannot say how many there are at the location
 *   shows sell from.
 * - **`scarce`** — below the minimum, *by the copy's figure*. The feature reads Shopify again, so
 *   the two can disagree in either direction.
 */
export type Availability =
  "available" | "disabled" | "untracked" | "no-location" | "scarce";

export function availability(
  entry: CatalogueEntry,
  settings: Settings | undefined,
): Availability {
  if (!entry.sku || !entry.sku.trim()) return "disabled";
  if (!entry.tracked) return "untracked";
  if (!entry.sellable) return "no-location";
  if (settings && (entry.available ?? 0) < settings.minFeatureStock) return "scarce";
  return "available";
}

/**
 * The matches for `term`, best first, at most `limit`. Removed entries never match. An empty term
 * matches nothing — a list of eleven hundred products is not a search result.
 *
 * `include` narrows the matches before the limit is applied, so hiding unavailable products does
 * not leave a short page when there were available ones further down.
 */
export function search(
  prepared: Prepared[],
  term: string,
  limit = 30,
  include: (entry: CatalogueEntry) => boolean = () => true,
): CatalogueEntry[] {
  const query = words(term);
  if (query.length === 0) return [];
  const folded = fold(term);

  const rank = (candidate: Prepared): number => {
    if (candidate.sku && candidate.sku === folded) return 0;
    if (candidate.title.startsWith(query[0]!)) return 1;
    return 2;
  };

  return prepared
    .filter(
      (candidate) =>
        !candidate.entry.removed &&
        include(candidate.entry) &&
        matches(candidate, query),
    )
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        a.title.localeCompare(b.title) ||
        a.entry.variantId.localeCompare(b.entry.variantId),
    )
    .slice(0, limit)
    .map((candidate) => candidate.entry);
}

/**
 * Folds a delta into the entries held: changed rows replace what was there, removed rows go.
 * Returns a new array, so a React state holding the old one sees the change.
 */
export function merge(
  held: CatalogueEntry[],
  delta: CatalogueEntry[],
): CatalogueEntry[] {
  if (delta.length === 0) return held;
  const byId = new Map(held.map((entry) => [entry.variantId, entry]));
  for (const entry of delta) {
    if (entry.removed) byId.delete(entry.variantId);
    else byId.set(entry.variantId, entry);
  }
  return [...byId.values()];
}

/**
 * How many units a featured lot would offer by default — the server's `defaultQuantity`, so the
 * sheet can say the number before the operator presses anything. **The server decides**: it reads
 * Shopify's stock again, and this is computed from the copy's figure, which may be older.
 */
export function suggestedQuantity(
  available: number | null,
  sharePercent: number,
): number {
  if (available === null || available < 2) return 0;
  return Math.max(
    Math.min(Math.floor((available * sharePercent) / 100), available - 1),
    1,
  );
}

/**
 * How old a stock figure is, in whole minutes, or null where there is no figure. The panel prints
 * it beside the number, because a figure an hour old is a guess and the operator should know
 * which they are looking at.
 */
export function ageMinutes(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  return Math.max(Math.floor((now - at) / 60_000), 0);
}
